import { useState } from 'react';
import {
  Alert,
  Box,
  Container,
  Header,
  Link,
  SpaceBetween,
  Tabs,
  Textarea,
} from '@amzn/awsui-components-react';
import CopyToClipboard from '@amzn/awsui-components-react/polaris/copy-to-clipboard';
import { TextFunction, useLocalizedText } from '../../hooks/localizedText';
import { LocalizedTrans } from '../../hooks/localization/localizedTrans';
import { toSafeWebsiteHref } from '../../utils/url';

const I18N = 'nonProfitReachOut';

/** What the coordinator knows about themselves, from the People API. Any value may be missing. */
export type ReachOutSender = {
  fullName?: string;
  email?: string;
  city?: string;
  /** The PAPI cost center name. */
  teamName?: string;
};

export type ReachOutPanelData = {
  sender: ReachOutSender;
  organizationName?: string;
  /** The organization's website as the cause API returns it; may lack a scheme. */
  website?: string;
};

export type ReachOutPanelProps = ReachOutPanelData & {
  /** Opens the tips help panel; the page owns the help-panel slot. */
  onOpenTips: () => void;
};

type TabId = 'outreach' | 'phone';

const PHONE_SECTIONS = ['introduce', 'purpose', 'ask', 'nextSteps'] as const;

/** Each template value, or its visible placeholder when we do not know it. */
function templateValues(t: TextFunction, { sender, organizationName }: ReachOutPanelData) {
  const valueOr = (value: string | undefined, placeholder: string) =>
    value?.trim() || t(`${I18N}.placeholders.${placeholder}`);
  return {
    organizationName: valueOr(organizationName, 'organizationName'),
    senderFullName: valueOr(sender.fullName, 'senderFullName'),
    senderEmail: valueOr(sender.email, 'senderEmail'),
    senderCity: valueOr(sender.city, 'senderCity'),
    senderTeamName: valueOr(sender.teamName, 'senderTeamName'),
  };
}

/**
 * The profile page's "Reach out" panel: an editable outreach email and phone talking points, each
 * filled in from what we know about the coordinator and the non-profit, each with a copy button.
 * Benevity gives us no email address for a non-profit, so the panel always points to its website.
 */
export function ReachOutPanel({ onOpenTips, ...data }: ReachOutPanelProps) {
  const { t } = useLocalizedText(['nonprofits', 'translation']);
  const [activeTabId, setActiveTabId] = useState<TabId>('outreach');
  // Null until the coordinator types, so the message keeps up with values that arrive late.
  const [editedMessage, setEditedMessage] = useState<string | null>(null);

  const values = templateValues(t, data);
  const message = editedMessage ?? t(`${I18N}.outreach.template`, values);
  const websiteHref = toSafeWebsiteHref(data.website);

  const phoneSections = PHONE_SECTIONS.map((section) => ({
    id: section,
    title: t(`${I18N}.phone.${section}.title`),
    body: t(`${I18N}.phone.${section}.body`, values),
  }));
  const talkingPoints = phoneSections.map(({ title, body }) => `${title}\n- ${body}`).join('\n\n');

  return (
    <Container
      data-testid="nonprofit-reach-out-panel"
      header={<Header variant="h2">{t(`${I18N}.title`)}</Header>}
    >
      <Tabs
        activeTabId={activeTabId}
        onChange={({ detail }) => setActiveTabId(detail.activeTabId as TabId)}
        tabs={[
          {
            id: 'outreach',
            label: t(`${I18N}.tabs.outreach`),
            content: (
              <SpaceBetween size="m">
                <Alert type="info" data-testid="nonprofit-reach-out-no-email">
                  <LocalizedTrans
                    i18nKey={`nonprofits:${I18N}.noEmailBanner`}
                    components={{
                      website: websiteHref ? (
                        <Link
                          external
                          href={websiteHref}
                          target="_blank"
                          data-testid="nonprofit-reach-out-website-link"
                          data-aci-analytics-name="nonProfitReachOut_websiteLinkClicked"
                        />
                      ) : (
                        <span />
                      ),
                    }}
                  />
                </Alert>
                <Header
                  variant="h3"
                  info={
                    <Link
                      variant="info"
                      data-testid="nonprofit-reach-out-tips-link"
                      data-aci-analytics-name="nonProfitReachOut_tipsOpened"
                      onFollow={onOpenTips}
                    >
                      {t(`${I18N}.outreach.tipsLink`)}
                    </Link>
                  }
                  actions={
                    <CopyToClipboard
                      variant="button"
                      data-testid="nonprofit-reach-out-copy-message"
                      data-aci-analytics-name="nonProfitReachOut_outreachMessageCopied"
                      copyButtonText={t(`${I18N}.outreach.copy`)}
                      copySuccessText={t(`${I18N}.outreach.copySuccess`)}
                      copyErrorText={t(`${I18N}.outreach.copyError`)}
                      textToCopy={message}
                    />
                  }
                >
                  {t(`${I18N}.outreach.heading`)}
                </Header>
                <Textarea
                  data-testid="nonprofit-reach-out-message"
                  ariaLabel={t(`${I18N}.outreach.messageLabel`)}
                  rows={14}
                  value={message}
                  onChange={({ detail }) => setEditedMessage(detail.value)}
                />
              </SpaceBetween>
            ),
          },
          {
            id: 'phone',
            label: t(`${I18N}.tabs.phone`),
            content: (
              <SpaceBetween size="m">
                <Header
                  variant="h3"
                  actions={
                    <CopyToClipboard
                      variant="button"
                      data-testid="nonprofit-reach-out-copy-talking-points"
                      data-aci-analytics-name="nonProfitReachOut_phoneTalkingPointsCopied"
                      copyButtonText={t(`${I18N}.phone.copy`)}
                      copySuccessText={t(`${I18N}.phone.copySuccess`)}
                      copyErrorText={t(`${I18N}.phone.copyError`)}
                      textToCopy={talkingPoints}
                    />
                  }
                >
                  {t(`${I18N}.phone.heading`)}
                </Header>
                {phoneSections.map(({ id, title, body }) => (
                  <div key={id} data-testid={`nonprofit-reach-out-phone-${id}`}>
                    <Box variant="h4">{title}</Box>
                    <ul>
                      <li>{body}</li>
                    </ul>
                  </div>
                ))}
              </SpaceBetween>
            ),
          },
        ]}
      />
    </Container>
  );
}
